import React, { useState, useEffect } from 'react';
import apiClient from '../api/api';
import { FaTrash, FaSearch, FaFilter, FaEye, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const AdminJobsPage = () => {
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const navigate = useNavigate();

    const fetchJobs = async () => {
        try {
            setLoading(true);
            const res = await apiClient.get('/admin/jobs');
            setJobs(res.data);
            setFilteredJobs(res.data);
        } catch (err) {
            alert("Failed to load jobs.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    useEffect(() => {
        let result = jobs;
        if (statusFilter !== 'all') {
            result = result.filter(j => j.status === statusFilter);
        }
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            result = result.filter(j => j.title.toLowerCase().includes(lower) || j.poster?.name?.toLowerCase().includes(lower));
        }
        setFilteredJobs(result);
    }, [searchTerm, statusFilter, jobs]);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to FORCE DELETE this job?")) return;
        try {
            await apiClient.delete(`/admin/jobs/${id}`);
            setJobs(jobs.filter(j => j.id !== id));
        } catch (err) {
            alert("Delete failed.");
        }
    };

    if (loading) return <div className="p-10 text-center font-bold text-slate-500">Loading Jobs...</div>;

    return (
        <div className="space-y-8 font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">Job Oversight</h1>
                    <p className="text-slate-500 mt-1 font-medium">Monitor and moderate all platform job postings.</p>
                </div>
                
                <div className="flex gap-3 w-full md:w-auto">
                    <div className="relative flex-grow md:w-64">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input 
                            type="text" 
                            placeholder="Search jobs..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm focus:ring-2 ring-emerald-500 outline-none font-bold text-slate-700"
                        />
                    </div>
                    <div className="relative">
                        <FaFilter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <select 
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="pl-10 pr-8 py-3 bg-white border border-slate-200 rounded-xl shadow-sm focus:ring-2 ring-emerald-500 outline-none font-bold text-slate-700 appearance-none cursor-pointer"
                        >
                            <option value="all">All Status</option>
                            <option value="open">Open</option>
                            <option value="running">Running</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                            <th className="px-8 py-5 text-xs font-black text-slate-400 uppercase tracking-widest">Job Title</th>
                            <th className="px-8 py-5 text-xs font-black text-slate-400 uppercase tracking-widest">Poster</th>
                            <th className="px-8 py-5 text-xs font-black text-slate-400 uppercase tracking-widest">Status</th>
                            <th className="px-8 py-5 text-xs font-black text-slate-400 uppercase tracking-widest">Date</th>
                            <th className="px-8 py-5 text-xs font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {filteredJobs.map((job) => (
                            <tr key={job.id} className="hover:bg-slate-50 transition-colors group">
                                <td className="px-8 py-5">
                                    <p className="font-black text-slate-800 text-sm line-clamp-1">{job.title}</p>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{job.post_model}</span>
                                </td>
                                <td className="px-8 py-5 text-sm font-bold text-slate-600">
                                    {job.poster?.name || 'Unknown'}
                                </td>
                                <td className="px-8 py-5">
                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                                        job.status === 'open' ? 'bg-emerald-100 text-emerald-700' :
                                        job.status === 'running' ? 'bg-blue-100 text-blue-700' :
                                        job.status === 'completed' ? 'bg-slate-100 text-slate-600' :
                                        'bg-red-100 text-red-700'
                                    }`}>
                                        {job.status}
                                    </span>
                                </td>
                                <td className="px-8 py-5 text-sm font-medium text-slate-500">
                                    {new Date(job.createdAt).toLocaleDateString()}
                                </td>
                                <td className="px-8 py-5 text-right">
                                    <div className="flex justify-end gap-2">
                                        <button 
                                            onClick={() => navigate(`/job/${job.id}`)}
                                            className="p-2.5 text-slate-500 hover:text-blue-600 bg-slate-100 hover:bg-blue-50 rounded-xl transition-all"
                                            title="View Job"
                                        >
                                            <FaEye />
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(job.id)}
                                            className="p-2.5 text-slate-400 hover:text-red-600 bg-slate-50 hover:bg-red-50 rounded-xl transition-all"
                                            title="Force Delete"
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredJobs.length === 0 && (
                    <div className="p-10 text-center text-slate-400 font-bold italic">No jobs found matching your criteria.</div>
                )}
            </div>
        </div>
    );
};

export default AdminJobsPage;
